/**
 * Der gerade geöffnete Zusammenhang (Belegung, Schüler, Zahler, Kurs, Lehrer, Familie, Standort).
 * Jeder Menüpunkt, der davon einen Satz zeigen kann, öffnet genau diesen.
 * Sammelseiten ohne einzelnen Satz bleiben die volle Liste.
 * Derselbe Zusammenhang wird angereichert, ein anderer ersetzt ihn.
 */
const KEY = 'mamv.selectedRecord';
const IDENTITY = ['enrollmentId', 'studentId', 'courseId', 'teacherId', 'guardianId', 'familyId', 'locationId'];
export const SELECTED_RECORD_EVENT = 'mamv-selected-record';

function parseLocalDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  }
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
}

function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function mondayIndex(date) {
  return (date.getDay() + 6) % 7;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function dayDistance(a, b) {
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}

function mod(value, base) {
  return ((value % base) + base) % base;
}

export function isBiweeklyRhythm(rhythm) {
  const text = String(rhythm || '').trim().toLowerCase();
  return text === 'biweekly' || text.startsWith('14');
}

/** Nächster Unterrichtstag um `reference`: Wochentag 0=Mo … 6=So, 14-tägig auf den Anker. */
export function lessonDateNear(reference, dayOfWeek, rhythm, anchorIso) {
  const day = Number(dayOfWeek);
  const ref = parseLocalDate(reference);
  if (!ref || !Number.isInteger(day) || day < 0 || day > 6) return null;
  const onWeek = addDays(ref, day - mondayIndex(ref));
  if (!isBiweeklyRhythm(rhythm)) return isoDate(onWeek);
  const anchor = parseLocalDate(anchorIso);
  if (!anchor) return isoDate(onWeek);
  const phase = addDays(anchor, mod(day - mondayIndex(anchor), 7));
  if (mod(dayDistance(onWeek, phase), 14) === 0) return isoDate(onWeek);
  const later = addDays(onWeek, 7);
  return isoDate(mod(dayDistance(later, phase), 14) === 0 ? later : addDays(onWeek, -7));
}

export function readSelectedRecord() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function blank(value) {
  return value == null || value === '';
}

function hasContext(record) {
  return IDENTITY.some((key) => record && !blank(record[key]));
}

/** Gemeinsame gesetzte Kennung und kein Widerspruch → derselbe Zusammenhang. */
function sameContext(prev, incoming) {
  let shared = false;
  for (const key of IDENTITY) {
    if (blank(incoming[key]) || blank(prev[key])) continue;
    if (prev[key] !== incoming[key]) return false;
    shared = true;
  }
  return shared;
}

function withoutBlanks(record) {
  const out = {};
  for (const [key, value] of Object.entries(record)) {
    if (!blank(value)) out[key] = value;
  }
  return out;
}

export function writeSelectedRecord(record) {
  try {
    const prev = readSelectedRecord();
    const incoming = record && typeof record === 'object' ? record : null;
    let next = null;
    if (incoming && prev && sameContext(prev, incoming)) {
      next = { ...prev };
      for (const [key, value] of Object.entries(incoming)) {
        if (blank(value)) delete next[key];
        else next[key] = value;
      }
    } else if (incoming) {
      next = withoutBlanks(incoming);
    }
    if (!hasContext(next)) sessionStorage.removeItem(KEY);
    else sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SELECTED_RECORD_EVENT));
  }
}

function clock(value) {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

/** Scrollposition, die den Block in der Mitte des Kalenderfensters zeigt. */
export function scrollDeltaToCenter(gridRect, blockRect, scrollTop = 0, scrollLeft = 0) {
  const top = blockRect.top - gridRect.top + scrollTop - (gridRect.height - blockRect.height) / 2;
  const left = blockRect.left - gridRect.left + scrollLeft - (gridRect.width - blockRect.width) / 2;
  return {
    top: Math.max(0, Math.round(top)),
    left: Math.max(0, Math.round(left)),
  };
}

/** Menüziel des gewählten Datensatzes. Seiten ohne einzelnen Satz bleiben unverändert. */
export function hrefForSelectedRecord(href, record, today = new Date()) {
  if (!href || !record) return href;
  const path = String(href).split('?')[0];
  if (path === '/calendar') {
    const date = lessonDateNear(today, record.dayOfWeek, record.rhythm, record.startDate);
    if (!date) return href;
    const parts = [`date=${date}`];
    if (record.courseId) parts.push(`focus_course=${record.courseId}`);
    const time = clock(record.startTime);
    if (time) parts.push(`focus_time=${time}`);
    return `/calendar?${parts.join('&')}`;
  }
  if (path === '/students' && record.studentId) return `/students/${record.studentId}`;
  if (path === '/courses' && record.courseId) return `/courses/${record.courseId}`;
  if (path === '/teachers' && record.teacherId) return `/teachers/${record.teacherId}`;
  if (path === '/locations' && record.locationId) return `/locations/${record.locationId}`;
  if (path === '/payer-accounts' && record.guardianId) return `/payer-accounts?guardian=${record.guardianId}`;
  if (path === '/guardians' && record.guardianId) return `/guardians?guardian=${record.guardianId}`;
  if (path === '/families' && record.familyId) return `/families?id=${record.familyId}`;
  if (path === '/anwesenheitslisten' && record.teacherId) return `/anwesenheitslisten?teacher_id=${record.teacherId}`;
  if (path === '/invoices' && record.guardianId) return `/invoices?guardian_id=${record.guardianId}`;
  if (path === '/unterrichtsverwaltung') {
    if (record.enrollmentId) return `/unterrichtsverwaltung?enrollment_id=${record.enrollmentId}`;
    if (record.studentId) return `/unterrichtsverwaltung?student_id=${record.studentId}`;
    if (record.guardianId) return `/unterrichtsverwaltung?guardian_id=${record.guardianId}`;
    if (record.courseId) return `/unterrichtsverwaltung?course_id=${record.courseId}`;
    if (record.teacherId) return `/unterrichtsverwaltung?teacher_id=${record.teacherId}`;
  }
  return href;
}
